import { Checkbox, Switch, Typography } from '@mui/material'
import { CustomerPaymentOperation, OperationInformation } from '@src/types/loan/loan-operation'
import { ChangeEvent, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ITableColumn } from './LoanCalendarTable-hook'
import styles from './LoanCalendarTable.module.css'

interface LoanCalendarTableHeadProps {
  columns: ITableColumn[]
  visibleColumns: Record<string, boolean>
  showMore: boolean
  onShowMoreChange: (value: boolean) => void
  rows: OperationInformation<CustomerPaymentOperation>[]
  selectedPaymentIds: Set<string>
  onSelectionChange: (ids: Set<string>) => void
}

const LoanCalendarTableHead = ({
  columns,
  visibleColumns,
  showMore,
  onShowMoreChange,
  rows,
  selectedPaymentIds,
  onSelectionChange,
}: LoanCalendarTableHeadProps) => {
  const { t } = useTranslation()

  const handleShowMoreChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onShowMoreChange(event.target.checked)
    },
    [onShowMoreChange],
  )

  const allRowIds = useMemo(() => new Set(rows.map((row) => row.operation.id)), [rows])
  const allSelected = allRowIds.size > 0 && selectedPaymentIds.size === allRowIds.size
  const indeterminate = selectedPaymentIds.size > 0 && !allSelected

  const handleSelectAllChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        onSelectionChange(new Set(allRowIds))
      } else {
        onSelectionChange(new Set())
      }
    },
    [allRowIds, onSelectionChange],
  )

  const visibleColumnsArray = columns.filter((col) => visibleColumns[col.field] !== false)

  return (
    <thead className={styles.table__header}>
      <tr className={styles.table__header_row}>
        <th className={`${styles.table__header_cell} ${styles.table__cell__checkbox}`}>
          <Checkbox
            checked={allSelected}
            indeterminate={indeterminate}
            onChange={handleSelectAllChange}
            size="small"
          />
        </th>
        {visibleColumnsArray.map((column) => (
          <th
            key={column.field}
            className={`${styles.table__header_cell} ${
              column.headerAlign === 'center'
                ? styles['table__header_cell--center']
                : column.headerAlign === 'right'
                  ? styles['table__header_cell--right']
                  : ''
            }`}
            style={{ width: column.width ? `${column.width}px` : column.field === 'description' ? 'auto' : undefined }}
          >
            {column.field === 'action' ? (
              <div className={styles.table__header_actions}>
                <Typography>{t('loan.payment.detail')}</Typography>
                <Switch checked={showMore} onChange={handleShowMoreChange} size="small" />
              </div>
            ) : (
              column.headerName
            )}
          </th>
        ))}
      </tr>
    </thead>
  )
}

export default LoanCalendarTableHead
